/**
 * @module utils/filters — Helpers compartilhados dos filtros declarativos do
 * BasePageHeader (header de tela). Centraliza o que era duplicado nas views CRM
 * (leads-tabs, relationship, ia-alteracoes, calendar, opportunities, overview).
 *
 * Conceitos:
 *  - "config de filtros": array de fields estáticos (label/key/type/backend/…),
 *    um por filtro. `type: 'date-range'` usa `startKey`/`endKey`.
 *  - "options": as opções dinâmicas de selects — resolvidas da store via
 *    `resolveFilterOptions` (precedência optionsResolver → optionsGetter → inline).
 *  - "backend vs front": fields com `backend: true` exigem refetch no servidor ao
 *    aplicar; os demais filtram no cliente. `buildBackendFilterKeys` +
 *    `didBackendFilterChange` decidem quando recarregar.
 *
 * Funções: `resolveFilterOptions`, `normalizeFilterValue`,
 * `buildBackendFilterKeys`, `didBackendFilterChange`.
 */

/**
 * Resolve as `options` dinâmicas de um config de filtros a partir da store
 * Vuex, devolvendo uma nova lista de fields pronta para o `BasePageHeader`.
 *
 * A store entra por parâmetro (não importa o singleton) — mantém o util puro,
 * testável e desacoplado. Como `store.getters[...]` / `optionsResolver(store)`
 * são acessados aqui dentro, o computed da view que chama esta função continua
 * reativo (o Vue 2 rastreia o getter acessado durante a avaliação).
 *
 * Cada field declara a fonte das options de uma destas formas (precedência):
 *   1. `optionsResolver: (store) => [...]`  → controle total (vários getters,
 *                                             filter/map/derivação).
 *   2. `optionsGetter: 'modulo/getter'`     → getter cru;
 *        + `optionsTransform?: (raw) => [...]` → opcional, formata/mapeia.
 *   3. (nenhum)                              → field inalterado (date-range,
 *                                             text, ou `options` estáticas).
 *
 * O resultado é sempre deep-clonado (protege o array vivo do Vuex de mutações).
 *
 * @example
 * filters() {
 *   return resolveFilterOptions({ fields: overviewFilters, store: this.$store })
 * }
 */
export function resolveFilterOptions({ fields, store } = {}) {
  if (!Array.isArray(fields)) return []

  return fields.map((field) => {
    let options

    if (typeof field.optionsResolver === 'function') {
      options = field.optionsResolver(store)
    } else if (field.optionsGetter) {
      const raw = (store && store.getters[field.optionsGetter]) || []
      options =
        typeof field.optionsTransform === 'function'
          ? field.optionsTransform(raw)
          : raw
    } else {
      return field
    }

    return { ...field, options: JSON.parse(JSON.stringify(options || [])) }
  })
}

/**
 * Normaliza um valor de filtro para comparação: `null`/`undefined`/`''`/`[]`
 * viram `null`; o resto vira a string JSON estável. Permite comparar "vazio em
 * qualquer forma" e valores estruturados (arrays/objetos) por igualdade.
 *
 * @param {*} value
 * @returns {string|null}
 */
export function normalizeFilterValue(value) {
  if (value === undefined || value === null || value === '') return null
  if (Array.isArray(value) && value.length === 0) return null
  return JSON.stringify(value)
}

/**
 * Extrai do config de filtros a lista plana de keys que exigem refetch no
 * backend (fields com `backend: true`), expandindo `date-range` em
 * `startKey`/`endKey`.
 *
 * @param {Array} config — array de fields do BasePageHeader.
 * @returns {string[]}
 */
export function buildBackendFilterKeys(config) {
  if (!Array.isArray(config)) return []

  return config.reduce((keys, field) => {
    if (!field.backend) return keys
    if (field.type === 'date-range') {
      return [...keys, field.startKey, field.endKey]
    }
    return [...keys, field.key]
  }, [])
}

/**
 * Diz se algum filtro de BACKEND mudou entre `prev` e `next` (comparando
 * normalizado). Use para só recarregar do servidor quando necessário.
 *
 * @param {{ keys: string[], prev?: object, next?: object }} params
 * @returns {boolean}
 * @example
 * const keys = buildBackendFilterKeys(leadsFilters)
 * if (didBackendFilterChange({ keys, prev, next })) reload()
 */
export function didBackendFilterChange({ keys, prev = {}, next = {} } = {}) {
  if (!Array.isArray(keys)) return false
  return keys.some(
    (key) => normalizeFilterValue(prev[key]) !== normalizeFilterValue(next[key])
  )
}
